import React, { useEffect, useState } from 'react';
import { Modal } from '@consta/uikit/Modal';
import { Text } from '@consta/uikit/Text';
import { TextField } from '@consta/uikit/TextField';
import { Button } from '@consta/uikit/Button';
import { Checkbox } from '@consta/uikit/Checkbox';
import { Combobox } from '@consta/uikit/Combobox';
import { IconCopy } from '@consta/icons/IconCopy';
import { useLazyGetFieldSelectorQuery } from '../../../../services/fieldService';
import { useLazyGetDepositSelectorQuery } from '../../../../services/depositService';
import { useLazyGetAreaSelectorQuery } from '../../../../services/areaService';
import { useLazyGetHorizonSelectorQuery } from '../../../../services/horizonService';

interface DuplicateModalProps {
  isOpen: boolean;
  entityType: 'deposit' | 'section' | 'horizon' | 'bed' | null;
  entityData: any | null;
  parentIds: {
    fieldUuid?: string;
    depositUuid?: string;
    areaUuid?: string;
    horizonUuid?: string;
  };
  duplicateName: string;
  deepCopy: boolean;
  targetParentId: string;
  onClose: () => void;
  onDuplicate: (name: string, deepCopy: boolean, targetParentId: string) => void;
  onNameChange: (value: any) => void;
  onDeepCopyChange: (checked: boolean) => void;
  onTargetParentIdChange: (value: string) => void;
}

interface SelectorItem {
  id: string;
  name: string;
}

const DuplicateModal: React.FC<DuplicateModalProps> = ({
  isOpen,
  entityType,
  entityData,
  parentIds,
  duplicateName,
  deepCopy,
  targetParentId,
  onClose,
  onDuplicate,
  onNameChange,
  onDeepCopyChange,
  onTargetParentIdChange
}) => {
  const [selectorItems, setSelectorItems] = useState<SelectorItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [triggerGetFieldSelector] = useLazyGetFieldSelectorQuery();
  const [triggerGetDepositSelector] = useLazyGetDepositSelectorQuery();
  const [triggerGetAreaSelector] = useLazyGetAreaSelectorQuery();
  const [triggerGetHorizonSelector] = useLazyGetHorizonSelectorQuery();

  useEffect(() => {
    if (!isOpen || !entityType) return;

    const loadSelectorItems = async () => {
      setIsLoading(true);
      try {
        let response: any;
        
        switch (entityType) {
          case 'deposit':
            response = await triggerGetFieldSelector({
              limit: 100,
              offset: 0
            }).unwrap();
            
            console.log('🏭 Данные месторождений:', response);
            
            const fieldItems = (response || []).map((item: any) => ({
              id: item.uuid,
              name: item.name
            }));
            
            console.log('📋 Элементы месторождений:', fieldItems);
            setSelectorItems(fieldItems);
            break;

          case 'section':
            response = await triggerGetDepositSelector({
              limit: 100,
              offset: 0
            }).unwrap();
            
            console.log('🛢️ Данные залежей:', response);
            
            const depositItems = (response || []).map((item: any) => ({
              id: item.uuid,
              name: item.name
            }));
            
            console.log('📋 Элементы залежей:', depositItems);
            setSelectorItems(depositItems);
            break;

          case 'horizon':
            response = await triggerGetAreaSelector({
              limit: 100,
              offset: 0
            }).unwrap();
            
            console.log('📐 Данные участков:', response);
            
            const areaItems = (response || []).map((item: any) => ({
              id: item.uuid,
              name: item.name
            }));
            
            console.log('📋 Элементы участков:', areaItems);
            setSelectorItems(areaItems);
            break;

          case 'bed':
            response = await triggerGetHorizonSelector({
              limit: 100,
              offset: 0
            }).unwrap();
            
            console.log('🗺️ Данные горизонтов:', response);
            
            const horizonItems = (response || []).map((item: any) => ({
              id: item.uuid,
              name: item.code || item.name || `Горизонт ${item.uuid}`
            }));
            
            console.log('📋 Элементы горизонтов:', horizonItems);
            setSelectorItems(horizonItems);
            break;
        }
      } catch (error) {
        console.error('❌ Ошибка загрузки данных для селектора:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSelectorItems();
  }, [isOpen, entityType]);

  const selectedItem = selectorItems.find(item => item.id === targetParentId) || null;

  const handleComboboxChange = (value: SelectorItem | null) => {
    console.log('🎯 Комбобокс изменился:', value);
    if (value) {
      onTargetParentIdChange(value.id);
    } else {
      onTargetParentIdChange('');
    }
  };

  const handleCheckboxChange = () => {
    const newValue = !deepCopy;
    console.log('✅ Чекбокс изменился:', { old: deepCopy, new: newValue });
    onDeepCopyChange(newValue);
  };

  const handleDuplicateClick = () => {
    console.log('🚀 Нажата кнопка Дублировать:', {
      duplicateName,
      deepCopy,
      targetParentId,
      selectedItem
    });

    if (!duplicateName.trim()) {
      console.warn('❌ Нельзя дублировать: отсутствует имя');
      return;
    }

    if (!targetParentId) {
      console.warn('❌ Нельзя дублировать: не выбрана родительская сущность');
      return;
    }

    // Убираем возможное двойное "(копия)"
    const cleanName = duplicateName.replace(/ \(копия\)$/, '');
    
    console.log('📤 Отправка данных на дублирование:', {
      cleanName,
      deepCopy,
      targetParentId
    });
    
    onDuplicate(cleanName, deepCopy, targetParentId);
  };

  if (!isOpen || !entityType || !entityData) return null;

  const getEntityTitle = () => {
    switch (entityType) {
      case 'deposit': return 'залежи';
      case 'section': return 'участка';
      case 'horizon': return 'горизонта';
      case 'bed': return 'пласта';
      default: return 'элемента';
    }
  };

  const getOriginalName = () => {
    return entityType === 'horizon' 
      ? entityData.code 
      : entityData.name;
  };

  const getSelectorLabel = () => {
    switch (entityType) {
      case 'deposit': return 'Месторождение';
      case 'section': return 'Залежь';
      case 'horizon': return 'Участок';
      case 'bed': return 'Горизонт';
      default: return 'Родительская сущность';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      hasOverlay
      onClickOutside={onClose}
      onEsc={onClose}
      className="duplicate-modal"
    >
      <div style={{ padding: 24, width: 500 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <Text size="xl" weight="semibold">
            Дублирование {getEntityTitle()}
          </Text>
        </div>

        <TextField
          label="Название копии"
          value={duplicateName}
          onChange={onNameChange}
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <Text size="s" view="secondary" style={{ marginBottom: 8 }}>
            {getSelectorLabel()}
          </Text>
          
          <Combobox
            placeholder={`Выберите ${getSelectorLabel().toLowerCase()}`}
            items={selectorItems}
            value={selectedItem}
            onChange={handleComboboxChange}
            isLoading={isLoading}
            getItemLabel={item => item.name}
            getItemKey={item => item.id}
            size="s"
          />
        </div>

        <Checkbox
          label="Глубокое копирование"
          checked={deepCopy}
          onChange={handleCheckboxChange}
          style={{ marginBottom: 16 }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button
            label="Отмена"
            view="ghost"
            onClick={onClose}
          />
          <Button
            label="Дублировать"
            view="primary"
            onClick={handleDuplicateClick}
            disabled={!duplicateName.trim() || !targetParentId}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DuplicateModal;